import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Tag,
  message,
  Spin,
  Typography,
  Button,
  Modal,
  Input,
  Space,
  Image,
  Alert,
  Divider,
  List,
  Tabs,
  Select,
} from 'antd';
import {
  CalendarOutlined,
  ReloadOutlined,
  TrophyOutlined,
  UserOutlined,
  WhatsAppOutlined,
  TeamOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  FormOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { categoriaLabel } from '../../../utils/categoriaLabel';

const { Text, Title } = Typography;

const formatMoney = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value ?? 0);

const DIAS_NOMBRE = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
};

const descripcionFranja = (franja) => {
  if (!franja) return '';
  if (franja.horaDesde && franja.horaHasta) return `${franja.nombre} (${franja.horaDesde}-${franja.horaHasta})`;
  if (!franja.horaDesde && franja.horaHasta) return `${franja.nombre} (hasta ${franja.horaHasta})`;
  if (franja.horaDesde && !franja.horaHasta) return `${franja.nombre} (después ${franja.horaDesde})`;
  return franja.nombre;
};

const JugadorMisTorneos = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [torneos, setTorneos] = useState([]);
  const [torneosEnCurso, setTorneosEnCurso] = useState([]);
  const [historialTorneos, setHistorialTorneos] = useState([]);
  const [inscripciones, setInscripciones] = useState([]);
  const [invitacionesRecibidas, setInvitacionesRecibidas] = useState([]);
  const [config, setConfig] = useState({ frontendUrl: '', mercadoPagoConfigurado: false });
  const [loading, setLoading] = useState(true);
  const [modalCompaneroVisible, setModalCompaneroVisible] = useState(false);
  const [modalInscripcionVisible, setModalInscripcionVisible] = useState(false);
  const [torneoSeleccionado, setTorneoSeleccionado] = useState(null);
  const [emailCompanero, setEmailCompanero] = useState('');
  const [companero, setCompanero] = useState(null);
  const [buscandoCompanero, setBuscandoCompanero] = useState(false);
  const [companeroNoEncontrado, setCompaneroNoEncontrado] = useState(false);
  const [enviandoInvitacion, setEnviandoInvitacion] = useState(false);
  const [inscribiendo, setInscribiendo] = useState(false);
  const [respondiendoInvitacion, setRespondiendoInvitacion] = useState(null);
  const [activeTab, setActiveTab] = useState('inscripciones');
  const [companerosDirectos, setCompanerosDirectos] = useState({});
  const [horarioInscripcion, setHorarioInscripcion] = useState({ diaSemana: null, franjaId: null });

  const torneosActivos = torneosEnCurso.filter((t) => t.tor_fase !== 'FINALIZADO');
  const historialIds = new Set(historialTorneos.map((t) => t.tor_id));
  const torneosEnCursoIds = new Set(torneosEnCurso.map((t) => t.tor_id));
  const inscripcionesEnCurso = inscripciones.filter((i) => !historialIds.has(i.tni_torneo_id));
  const inscripcionesFinalizadas = inscripciones.filter((i) => historialIds.has(i.tni_torneo_id));

  const loadData = async () => {
    setLoading(true);
    try {
      const [configRes, torneosRes, enCursoRes, historialRes, inscripcionesRes, invitacionesRes] = await Promise.all([
        apiClient.get('/torneos/mi/config'),
        apiClient.get('/torneos/mi/torneos'),
        apiClient.get('/torneos/mi/en-curso'),
        apiClient.get('/torneos/mi/historial'),
        apiClient.get('/torneos/mi/inscripciones'),
        apiClient.get('/torneos/mi/invitaciones-companero'),
      ]);

      if (configRes.ok) {
        setConfig(await configRes.json());
      }
      if (torneosRes.ok) {
        const data = await torneosRes.json();
        setTorneos(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar torneos');
        setTorneos([]);
      }
      if (enCursoRes.ok) {
        const data = await enCursoRes.json();
        setTorneosEnCurso(Array.isArray(data) ? data : []);
      } else {
        setTorneosEnCurso([]);
      }
      if (historialRes.ok) {
        const data = await historialRes.json();
        setHistorialTorneos(Array.isArray(data) ? data : []);
      } else {
        setHistorialTorneos([]);
      }
      if (inscripcionesRes.ok) {
        const data = await inscripcionesRes.json();
        setInscripciones(Array.isArray(data) ? data : []);
      }
      if (invitacionesRes.ok) {
        const data = await invitacionesRes.json();
        setInvitacionesRecibidas(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error(error);
      message.error('Error al cargar torneos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const mp = searchParams.get('mp');
    if (mp === 'success') {
      message.success('Seña abonada. Tu inscripción quedó confirmada. El saldo se cobra en el club.');
      setSearchParams({}, { replace: true });
      loadData();
    } else if (mp === 'pending') {
      message.info('El pago está pendiente. La inscripción se confirma cuando Mercado Pago acredite la seña.');
      setSearchParams({}, { replace: true });
      loadData();
    } else if (mp === 'failure') {
      message.error('El pago no se completó. La inscripción no quedó registrada. Podés intentar de nuevo.');
      setSearchParams({}, { replace: true });
      loadData();
    }
  }, [searchParams, setSearchParams]);

  const abrirModalCompanero = (torneo) => {
    setTorneoSeleccionado(torneo);
    setEmailCompanero('');
    setCompanero(null);
    setCompaneroNoEncontrado(false);
    setModalCompaneroVisible(true);
  };

  const abrirModalInscripcion = (torneo) => {
    setTorneoSeleccionado(torneo);
    setHorarioInscripcion({ diaSemana: null, franjaId: null });
    setModalInscripcionVisible(true);
  };

  const buscarCompanero = async () => {
    if (!emailCompanero?.trim()) {
      message.warning('Ingresá el email de tu compañero');
      return;
    }
    setBuscandoCompanero(true);
    setCompanero(null);
    setCompaneroNoEncontrado(false);
    try {
      const response = await apiClient.get(
        `/torneos/mi/companero?email=${encodeURIComponent(emailCompanero.trim())}&torneoId=${torneoSeleccionado.tor_id}`
      );
      if (response.ok) {
        setCompanero(await response.json());
      } else if (response.status === 404) {
        setCompaneroNoEncontrado(true);
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al buscar compañero');
      }
    } catch (error) {
      console.error(error);
      message.error('Error al buscar compañero');
    } finally {
      setBuscandoCompanero(false);
    }
  };

  const getWhatsAppMensaje = (torneo) => {
    const baseUrl = config.frontendUrl || window.location.origin;
    const link = `${baseUrl}/#/jugador/mis-torneos`;
    const fecha = torneo.tor_fecha ? dayjs(torneo.tor_fecha).format('DD/MM/YYYY') : '';
    return `¡Hola! Te invito a anotarnos juntos en el torneo "${torneo.tor_nombre}"${fecha ? ` del ${fecha}` : ''}. Registrate en el club e inscribite acá: ${link}`;
  };

  const compartirWhatsApp = (torneo) => {
    const texto = encodeURIComponent(getWhatsAppMensaje(torneo));
    window.open(`https://wa.me/?text=${texto}`, '_blank', 'noopener,noreferrer');
  };

  const enviarInvitacionCompanero = async () => {
    if (!companero) return;
    setEnviandoInvitacion(true);
    try {
      const response = await apiClient.post('/torneos/mi/invitaciones-companero', {
        torneoId: torneoSeleccionado.tor_id,
        companeroId: companero.jcl_id,
      });
      const data = await response.json();
      if (!response.ok) {
        message.error(data.message || 'Error al enviar invitación');
        return;
      }
      message.success(data.message || 'Invitación enviada');
      setModalCompaneroVisible(false);
      loadData();
    } catch (error) {
      console.error(error);
      message.error('Error al enviar invitación');
    } finally {
      setEnviandoInvitacion(false);
    }
  };

  const seleccionarCompaneroDirecto = () => {
    if (!companero || !torneoSeleccionado) return;
    setCompanerosDirectos((prev) => ({
      ...prev,
      [torneoSeleccionado.tor_id]: companero,
    }));
    message.success('Compañero seleccionado. Ya podés inscribirte.');
    setModalCompaneroVisible(false);
  };

  const handleInscribirme = async () => {
    const torneo = torneoSeleccionado;
    if (!torneo) return;

    const companeroDirecto = companerosDirectos[torneo.tor_id];
    const invitacion = torneo.invitacion_companero;
    const esDirecto = Boolean(torneo.tor_companero_directo);

    let companeroId = null;
    if (esDirecto) {
      companeroId = companeroDirecto?.jcl_id;
      if (!companeroId) {
        message.warning('Primero elegí a tu compañero');
        return;
      }
    } else {
      if (!invitacion || invitacion.tic_estado !== 'ACEPTADA') {
        message.warning('Tu compañero debe aceptar la invitación antes de inscribirte');
        return;
      }
      companeroId = invitacion.tic_companero_id;
    }

    const pideHorario = Boolean(torneo.tor_elegir_horario_inscripcion);
    if (pideHorario && (horarioInscripcion.diaSemana == null || horarioInscripcion.franjaId == null)) {
      message.warning('Elegí el día y la franja horaria para inscribirte');
      return;
    }

    setInscribiendo(true);
    try {
      const response = await apiClient.post('/torneos/mi/inscripciones', {
        torneoId: torneo.tor_id,
        companeroId,
        ...(pideHorario
          ? { diaSemana: horarioInscripcion.diaSemana, franjaId: horarioInscripcion.franjaId }
          : {}),
      });
      const data = await response.json();
      if (!response.ok) {
        message.error(data.message || 'Error al inscribirse');
        return;
      }

      if (data.requiereMercadoPago && data.init_point) {
        message.info('Te redirigimos a Mercado Pago para abonar la seña');
        window.location.href = data.init_point;
        return;
      }

      message.success(data.message || 'Inscripción registrada');
      setModalInscripcionVisible(false);
      setCompanerosDirectos((prev) => {
        const next = { ...prev };
        delete next[torneo.tor_id];
        return next;
      });
      loadData();
    } catch (error) {
      console.error(error);
      message.error('Error al inscribirse');
    } finally {
      setInscribiendo(false);
    }
  };

  const handlePagarSenia = async (torneo) => {
    if (!torneo?.pendiente_companero_id) {
      abrirModalInscripcion(torneo);
      return;
    }
    setTorneoSeleccionado(torneo);
    setInscribiendo(true);
    try {
      const pideHorario = Boolean(torneo.tor_elegir_horario_inscripcion);
      const response = await apiClient.post('/torneos/mi/inscripciones', {
        torneoId: torneo.tor_id,
        companeroId: torneo.pendiente_companero_id,
        ...(pideHorario && torneo.pendiente_dia_semana != null && torneo.pendiente_franja_id != null
          ? { diaSemana: torneo.pendiente_dia_semana, franjaId: torneo.pendiente_franja_id }
          : {}),
      });
      const data = await response.json();
      if (!response.ok) {
        message.error(data.message || 'Error al continuar el pago');
        return;
      }
      if (data.requiereMercadoPago && data.init_point) {
        message.info('Te redirigimos a Mercado Pago para abonar la seña');
        window.location.href = data.init_point;
        return;
      }
      loadData();
    } catch (error) {
      console.error(error);
      message.error('Error al continuar el pago');
    } finally {
      setInscribiendo(false);
    }
  };

  const responderInvitacion = async (invitacionId, aceptar) => {
    setRespondiendoInvitacion(invitacionId);
    try {
      const accion = aceptar ? 'aceptar' : 'rechazar';
      const response = await apiClient.post(`/torneos/mi/invitaciones-companero/${invitacionId}/${accion}`);
      const data = await response.json();
      if (!response.ok) {
        message.error(data.message || 'Error al responder invitación');
        return;
      }
      message.success(data.message || (aceptar ? 'Invitación aceptada' : 'Invitación rechazada'));
      loadData();
    } catch (error) {
      console.error(error);
      message.error('Error al responder invitación');
    } finally {
      setRespondiendoInvitacion(null);
    }
  };

  const renderEstadoInvitacion = (invitacion) => {
    if (!invitacion) return null;
    if (invitacion.tic_estado === 'ACEPTADA') {
      return (
        <Tag color="green">
          Compañero: {invitacion.companero_nombre} {invitacion.companero_apellido} (aceptado)
        </Tag>
      );
    }
    if (invitacion.tic_estado === 'PENDIENTE') {
      return (
        <Tag color="orange">
          Esperando que {invitacion.companero_nombre} {invitacion.companero_apellido} acepte
        </Tag>
      );
    }
    return null;
  };

  const renderEstadoInscripcion = (item) => {
    if (item.espera_senia_mp || (item.tni_estado === 'PENDIENTE' && item.tni_requiere_mp)) {
      return <Tag color="orange">Pendiente de seña</Tag>;
    }
    if (item.cobro_pendiente) {
      return <Tag color="gold">Inscripta · saldo en el club</Tag>;
    }
    if (item.tni_estado === 'PAGADO') return <Tag color="green">Confirmada</Tag>;
    if (item.tni_estado === 'PENDIENTE') return <Tag color="orange">Pendiente de pago</Tag>;
    return <Tag>{item.tni_estado}</Tag>;
  };

  const renderTorneoCard = (torneo) => {
    const fotoSrc = torneo.tor_foto_base64
      ? `data:${torneo.tor_foto_tipo || 'image/jpeg'};base64,${torneo.tor_foto_base64}`
      : null;
    const categoriasTexto = torneo.tor_inscripcion_libre
      ? 'Inscripción libre (todas las categorías)'
      : (torneo.categorias_permitidas || [])
        .map((c) => categoriaLabel(c))
        .join(', ');
    const inscripcionCerrada = torneo.inscripcion_abierta === false;
    const esCompaneroDirecto = Boolean(torneo.tor_companero_directo);
    const companeroDirectoElegido = companerosDirectos[torneo.tor_id] || null;
    const companeroListo = esCompaneroDirecto
      ? Boolean(companeroDirectoElegido)
      : Boolean(torneo.companero_aceptado);
    const puedeInscribirse = !inscripcionCerrada && companeroListo && !torneo.ya_inscripto && !torneo.espera_senia_mp;
    const esperaSenia = Boolean(torneo.espera_senia_mp);

    return (
      <Col xs={24} md={12} key={torneo.tor_id}>
        <Card
          style={{ height: '100%', background: '#2d2d2d', border: '1px solid #434343' }}
          bodyStyle={{ padding: 16 }}
        >
          <Row gutter={16}>
            <Col flex="88px">
              {fotoSrc ? (
                <Image
                  src={fotoSrc}
                  alt={torneo.tor_nombre}
                  width={72}
                  height={72}
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                  preview
                />
              ) : (
                <div
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: 8,
                    background: 'rgba(255,255,255,0.06)',
                    display: 'grid',
                    placeItems: 'center',
                  }}
                >
                  <TrophyOutlined style={{ fontSize: 28, color: '#8c8c8c' }} />
                </div>
              )}
            </Col>
            <Col flex="auto">
              <Title level={5} style={{ color: '#fff', margin: 0 }}>
                {torneo.tor_nombre}
              </Title>
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                <CalendarOutlined /> Fecha torneo: {dayjs(torneo.tor_fecha).format('DD/MM/YYYY')}
              </Text>
              <Text type="secondary" style={{ display: 'block' }}>
                Inscripción: {dayjs(torneo.tor_inscripcion_desde).format('DD/MM/YYYY')} -{' '}
                {dayjs(torneo.tor_inscripcion_hasta).format('DD/MM/YYYY')}
              </Text>
              <div style={{ marginTop: 8 }}>
                <Tag color={torneo.categoria_color || 'default'}>{torneo.categoria_descripcion}</Tag>
                {inscripcionCerrada && (
                  <Tag color="red" style={{ marginLeft: 4 }}>Inscripción cerrada</Tag>
                )}
                {categoriasTexto && (
                  <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                    Categorías habilitadas: {categoriasTexto}
                  </Text>
                )}
              </div>
              <div style={{ marginTop: 8 }}>
                <Text style={{ color: '#d8ff3e' }}>
                  {torneo.tor_con_senia
                    ? `Seña: ${formatMoney(torneo.tor_senia)} (Mercado Pago)`
                    : `Inscripción: ${formatMoney(torneo.tor_precio_pareja)} (pago en el club)`}
                </Text>
              </div>
              {esCompaneroDirecto && companeroDirectoElegido && (
                <div style={{ marginTop: 8 }}>
                  <Tag color="green">
                    Compañero: {companeroDirectoElegido.jcl_nombre}{' '}
                    {companeroDirectoElegido.jcl_apellido}
                  </Tag>
                </div>
              )}
              {Boolean(torneo.tor_elegir_horario_inscripcion) && (
                <div style={{ marginTop: 8 }}>
                  <Tag color="cyan">El jugador elige día y franja</Tag>
                </div>
              )}
              {!esCompaneroDirecto && torneo.invitacion_companero && (
                <div style={{ marginTop: 8 }}>{renderEstadoInvitacion(torneo.invitacion_companero)}</div>
              )}
              {esperaSenia && (
                <Alert
                  type="warning"
                  showIcon
                  message="Pendiente de seña"
                  description="La inscripción se confirma cuando abonés la seña en Mercado Pago."
                  style={{ marginTop: 12 }}
                />
              )}
              {inscripcionCerrada && !torneo.ya_inscripto && !esperaSenia && (
                <Alert
                  type="warning"
                  showIcon
                  message="Torneo cerrado"
                  description="Este torneo ya no acepta nuevas inscripciones."
                  style={{ marginTop: 12 }}
                />
              )}
            </Col>
          </Row>

          <Space wrap style={{ marginTop: 16 }}>
            {torneo.ya_inscripto ? (
              <Tag color="blue">Ya inscripto</Tag>
            ) : esperaSenia ? (
              <>
                <Tag color="orange">Pendiente de seña</Tag>
                {torneo.puede_pagar_senia && (
                  <Button type="primary" loading={inscribiendo} onClick={() => handlePagarSenia(torneo)}>
                    Pagar seña
                  </Button>
                )}
              </>
            ) : inscripcionCerrada ? (
              <Tag color="red">Inscripción cerrada</Tag>
            ) : (
              <>
                <Button icon={<TeamOutlined />} onClick={() => abrirModalCompanero(torneo)}>
                  {esCompaneroDirecto
                    ? (companeroDirectoElegido ? 'Cambiar compañero' : 'Elegir compañero')
                    : 'Compañero'}
                </Button>
                <Button
                  type="primary"
                  onClick={() => abrirModalInscripcion(torneo)}
                  disabled={!puedeInscribirse}
                >
                  Inscribirme
                </Button>
              </>
            )}
          </Space>
        </Card>
      </Col>
    );
  };

  const renderInvitacionesCompanero = () => {
    if (invitacionesRecibidas.length === 0) return null;
    return (
      <>
        <Title level={4} style={{ color: '#fff', marginBottom: 12 }}>
          Invitaciones de compañero
        </Title>
        <List
          dataSource={invitacionesRecibidas}
          style={{ marginBottom: 24 }}
          renderItem={(item) => (
            <List.Item
              style={{
                background: '#2d2d2d',
                border: '1px solid #434343',
                borderRadius: 8,
                marginBottom: 12,
                padding: 16,
              }}
            >
              <div style={{ width: '100%' }}>
                <Text strong style={{ color: '#fff' }}>
                  {item.solicitante_nombre} {item.solicitante_apellido}
                </Text>
                <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                  Te invitó como compañero en <strong>{item.tor_nombre}</strong>
                  {item.tor_fecha ? ` (${dayjs(item.tor_fecha).format('DD/MM/YYYY')})` : ''}
                </Text>
                <Space style={{ marginTop: 12 }}>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={respondiendoInvitacion === item.tic_id}
                    onClick={() => responderInvitacion(item.tic_id, true)}
                  >
                    Aceptar
                  </Button>
                  <Button
                    danger
                    icon={<CloseOutlined />}
                    loading={respondiendoInvitacion === item.tic_id}
                    onClick={() => responderInvitacion(item.tic_id, false)}
                  >
                    Rechazar
                  </Button>
                </Space>
              </div>
            </List.Item>
          )}
        />
      </>
    );
  };

  const renderTorneoEnCursoItem = (item) => (
    <List.Item
      style={{
        background: '#2d2d2d',
        border: '1px solid #434343',
        borderRadius: 8,
        marginBottom: 12,
        padding: 16,
      }}
    >
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Text strong style={{ color: '#fff' }}>{item.tor_nombre}</Text>
          <div>
            <Text type="secondary">
              {item.tor_fecha ? dayjs(item.tor_fecha).format('DD/MM/YYYY') : ''}
            </Text>
          </div>
          <Space size={4} style={{ marginTop: 4 }} wrap>
            <Tag color="blue">{item.fase_label}</Tag>
            {item.modo_seguimiento && (
              <Tag color="purple">Seguimiento</Tag>
            )}
          </Space>
        </div>
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/jugador/mis-torneos/${item.tor_id}`)}
        >
          {item.modo_seguimiento ? 'Ver llave' : 'Ver torneo'}
        </Button>
      </div>
    </List.Item>
  );

  const renderTorneoHistorialItem = (item) => (
    <List.Item
      style={{
        background: '#2d2d2d',
        border: '1px solid #434343',
        borderRadius: 8,
        marginBottom: 12,
        padding: 16,
      }}
    >
      <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Text strong style={{ color: '#fff' }}>{item.tor_nombre}</Text>
          <div>
            <Text type="secondary">
              {item.tor_fecha ? dayjs(item.tor_fecha).format('DD/MM/YYYY') : ''}
            </Text>
          </div>
          <Space size={4} style={{ marginTop: 4 }} wrap>
            <Tag color="gold">{item.fase_label || 'Finalizado'}</Tag>
            {item.es_participante ? (
              <Tag color="blue">Participaste</Tag>
            ) : (
              <Tag color="purple">Resultados</Tag>
            )}
          </Space>
          {item.campeon_pareja && (
            <div style={{ marginTop: 6 }}>
              <Text type="secondary">
                <TrophyOutlined style={{ marginRight: 6, color: '#faad14' }} />
                Campeones: {item.campeon_pareja}
              </Text>
            </div>
          )}
        </div>
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => navigate(`/jugador/mis-torneos/${item.tor_id}`)}
        >
          Ver resultados
        </Button>
      </div>
    </List.Item>
  );

  const renderMisInscripciones = (items) => {
    if (items.length === 0) return null;
    return (
      <>
        <Divider style={{ borderColor: '#434343', margin: '8px 0 16px' }} />
        <Title level={4} style={{ color: '#fff', marginBottom: 12 }}>
          Mis inscripciones
        </Title>
        <List
          dataSource={items}
          renderItem={(item) => (
            <List.Item
              style={{
                background: '#2d2d2d',
                border: '1px solid #434343',
                borderRadius: 8,
                marginBottom: 12,
                padding: 16,
              }}
            >
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                  <div>
                    <Text strong style={{ color: '#fff' }}>{item.tor_nombre}</Text>
                    <div>
                      <Text type="secondary">
                        {item.tor_fecha ? dayjs(item.tor_fecha).format('DD/MM/YYYY') : ''}
                      </Text>
                    </div>
                  </div>
                  {renderEstadoInscripcion(item)}
                </div>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    <TeamOutlined /> Pareja: {item.jugador_nombre} {item.jugador_apellido} +{' '}
                    {item.companero_nombre} {item.companero_apellido}
                  </Text>
                </div>
                {(item.tni_dia_semana != null || item.franja_nombre) && (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">
                      Horario:{' '}
                      {[
                        item.tni_dia_semana != null ? DIAS_NOMBRE[item.tni_dia_semana] : null,
                        item.franja_nombre,
                      ].filter(Boolean).join(' · ')}
                    </Text>
                  </div>
                )}
                <div style={{ marginTop: 4 }}>
                  <Text type="secondary">
                    {item.tni_requiere_mp
                      ? `Seña: ${formatMoney(item.tor_senia)} · Precio: ${formatMoney(item.tor_precio_pareja ?? item.tni_monto)}`
                      : `Monto: ${formatMoney(item.tni_monto)}`}
                  </Text>
                </div>
                {item.cobro_pendiente && item.tni_requiere_mp ? (
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">Saldo a pagar en el club: {formatMoney(item.monto_cobrar)}</Text>
                  </div>
                ) : null}
                {historialIds.has(item.tni_torneo_id) || torneosEnCursoIds.has(item.tni_torneo_id) ? (
                  <Button
                    type="link"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/jugador/mis-torneos/${item.tni_torneo_id}`)}
                    style={{ paddingLeft: 0, marginTop: 8 }}
                  >
                    Ver torneo
                  </Button>
                ) : null}
              </div>
            </List.Item>
          )}
        />
      </>
    );
  };

  const tabSubtitulos = {
    inscripciones: 'Torneos activos con inscripción abierta',
    'en-curso': 'Tus torneos activos y eliminatorias abiertas para seguir en vivo',
    historial: 'Resultados de todos los torneos finalizados',
  };

  return (
    <MainLayout>
      <div style={{ maxWidth: 980, margin: '0 auto', padding: '16px 12px 32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <Title level={3} style={{ color: '#fff', margin: 0 }}>
              Mis Torneos
            </Title>
            <Text type="secondary">{tabSubtitulos[activeTab]}</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
            Actualizar
          </Button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        ) : (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'inscripciones',
                label: (
                  <span>
                    <FormOutlined /> Inscripciones
                  </span>
                ),
                children: (
                  <>
                    {renderInvitacionesCompanero()}
                    <Title level={4} style={{ color: '#fff', marginBottom: 12 }}>
                      Inscripción abierta
                    </Title>
                    {torneos.length === 0 ? (
                      <Card style={{ background: '#2d2d2d', border: '1px solid #434343' }}>
                        <Text type="secondary">
                          No hay torneos disponibles para tu categoría en este momento.
                        </Text>
                      </Card>
                    ) : (
                      <Row gutter={[16, 16]}>{torneos.map(renderTorneoCard)}</Row>
                    )}
                  </>
                ),
              },
              {
                key: 'en-curso',
                label: (
                  <span>
                    <ClockCircleOutlined /> Torneos en curso
                  </span>
                ),
                children: (
                  <>
                    {torneosActivos.length === 0 ? (
                      <Card style={{ background: '#2d2d2d', border: '1px solid #434343', marginBottom: 16 }}>
                        <Text type="secondary">
                          No hay torneos en curso ni eliminatorias disponibles para seguir en este momento.
                        </Text>
                      </Card>
                    ) : (
                      <List
                        dataSource={torneosActivos}
                        style={{ marginBottom: 8 }}
                        renderItem={renderTorneoEnCursoItem}
                      />
                    )}
                    {renderMisInscripciones(inscripcionesEnCurso)}
                  </>
                ),
              },
              {
                key: 'historial',
                label: (
                  <span>
                    <HistoryOutlined /> Historial de torneos
                  </span>
                ),
                children: (
                  <>
                    {historialTorneos.length === 0 ? (
                      <Card style={{ background: '#2d2d2d', border: '1px solid #434343', marginBottom: 16 }}>
                        <Text type="secondary">Todavía no hay torneos finalizados.</Text>
                      </Card>
                    ) : (
                      <List
                        dataSource={historialTorneos}
                        style={{ marginBottom: 8 }}
                        renderItem={renderTorneoHistorialItem}
                      />
                    )}
                    {renderMisInscripciones(inscripcionesFinalizadas)}
                  </>
                ),
              },
            ]}
          />
        )}

        <Modal
          title="Buscar compañero"
          open={modalCompaneroVisible}
          onCancel={() => setModalCompaneroVisible(false)}
          footer={null}
          destroyOnClose
        >
          {torneoSeleccionado && (
            <>
              <Text strong>{torneoSeleccionado.tor_nombre}</Text>
              <div style={{ marginTop: 12, marginBottom: 16 }}>
                <Text type="secondary">Buscá a tu compañero por email</Text>
                <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="email@ejemplo.com"
                    value={emailCompanero}
                    onChange={(e) => {
                      setEmailCompanero(e.target.value);
                      setCompanero(null);
                      setCompaneroNoEncontrado(false);
                    }}
                    onPressEnter={buscarCompanero}
                  />
                  <Button type="primary" onClick={buscarCompanero} loading={buscandoCompanero}>
                    Buscar
                  </Button>
                </Space.Compact>
              </div>

              {companeroNoEncontrado && (
                <>
                  <Alert
                    type="warning"
                    showIcon
                    message="No existe jugador"
                    description="No encontramos un jugador registrado con ese email. Invitalo por WhatsApp para que se registre en el club."
                    style={{ marginBottom: 16 }}
                  />
                  <Button
                    type="primary"
                    icon={<WhatsAppOutlined />}
                    onClick={() => compartirWhatsApp(torneoSeleccionado)}
                    block
                  >
                    Invitar por WhatsApp
                  </Button>
                </>
              )}

              {companero && (
                <>
                  <Alert
                    type="success"
                    showIcon
                    message={`${companero.jcl_nombre} ${companero.jcl_apellido}`}
                    description={
                      <>
                        Email: {companero.jcl_email}
                        {companero.categoria_nombre && (
                          <div>Categoría: {companero.categoria_nombre}</div>
                        )}
                        <div style={{ marginTop: 8 }}>
                          {torneoSeleccionado.tor_companero_directo
                            ? 'Vas a poder inscribirte con este compañero sin que tenga que aceptar una invitación.'
                            : 'Se enviará una invitación. Tu compañero deberá aceptarla para poder inscribirse.'}
                        </div>
                      </>
                    }
                    style={{ marginBottom: 16 }}
                  />
                  {torneoSeleccionado.tor_companero_directo ? (
                    <Button
                      type="primary"
                      icon={<TeamOutlined />}
                      onClick={seleccionarCompaneroDirecto}
                      block
                    >
                      Elegir compañero
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      icon={<TeamOutlined />}
                      onClick={enviarInvitacionCompanero}
                      loading={enviandoInvitacion}
                      block
                    >
                      Enviar invitación a compañero
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Modal>

        <Modal
          title="Inscribirme al torneo"
          open={modalInscripcionVisible}
          onCancel={() => setModalInscripcionVisible(false)}
          footer={null}
          destroyOnClose
        >
          {torneoSeleccionado && (
            <>
              <Text strong>{torneoSeleccionado.tor_nombre}</Text>
              {(() => {
                const esDirecto = Boolean(torneoSeleccionado.tor_companero_directo);
                const companeroDirecto = companerosDirectos[torneoSeleccionado.tor_id];
                const invitacion = torneoSeleccionado.invitacion_companero;
                const listo = esDirecto
                  ? Boolean(companeroDirecto)
                  : invitacion?.tic_estado === 'ACEPTADA';
                const nombrePareja = esDirecto
                  ? (companeroDirecto
                    ? `${companeroDirecto.jcl_nombre} ${companeroDirecto.jcl_apellido}`
                    : null)
                  : (invitacion?.tic_estado === 'ACEPTADA'
                    ? `${invitacion.companero_nombre} ${invitacion.companero_apellido}`
                    : null);

                return (
                  <>
                    {listo ? (
                      <Alert
                        type="success"
                        showIcon
                        message="Pareja confirmada"
                        description={
                          <>
                            Vas a inscribirte con <strong>{nombrePareja}</strong>
                          </>
                        }
                        style={{ marginTop: 16, marginBottom: 16 }}
                      />
                    ) : (
                      <Alert
                        type="warning"
                        showIcon
                        message="Compañero pendiente"
                        description={
                          esDirecto
                            ? 'Primero elegí a tu compañero desde el botón Elegir compañero.'
                            : 'Primero buscá a tu compañero y esperá que acepte la invitación.'
                        }
                        style={{ marginTop: 16, marginBottom: 16 }}
                      />
                    )}

                    {Boolean(torneoSeleccionado.tor_elegir_horario_inscripcion) && (
                      <div style={{ marginBottom: 16 }}>
                        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                          Elegí el día y la franja horaria en la que pueden jugar
                        </Text>
                        <Space direction="vertical" style={{ width: '100%' }} size={8}>
                          <Select
                            placeholder="Día"
                            value={horarioInscripcion.diaSemana ?? undefined}
                            onChange={(value) => setHorarioInscripcion((prev) => ({ ...prev, diaSemana: value ?? null }))}
                            allowClear
                            style={{ width: '100%' }}
                            options={(torneoSeleccionado.dias_disponibles || []).map((d) => ({
                              value: d.diaSemana,
                              label: d.nombre,
                            }))}
                            optionRender={(option) => {
                              const dia = (torneoSeleccionado.dias_disponibles || []).find((d) => d.diaSemana === option.value);
                              return (
                                <Space>
                                  <span
                                    style={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: 999,
                                      background: dia?.color || '#555',
                                      display: 'inline-block',
                                    }}
                                  />
                                  {option.label}
                                </Space>
                              );
                            }}
                          />
                          <Select
                            placeholder="Franja horaria"
                            value={horarioInscripcion.franjaId ?? undefined}
                            onChange={(value) => setHorarioInscripcion((prev) => ({ ...prev, franjaId: value ?? null }))}
                            allowClear
                            style={{ width: '100%' }}
                            options={(torneoSeleccionado.franjas_horarias || []).map((f) => ({
                              value: f.id,
                              label: descripcionFranja(f),
                            }))}
                            optionRender={(option) => {
                              const franja = (torneoSeleccionado.franjas_horarias || []).find((f) => f.id === option.value);
                              return (
                                <Space>
                                  <span
                                    style={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: 999,
                                      background: franja?.color || '#555',
                                      display: 'inline-block',
                                    }}
                                  />
                                  {option.label}
                                </Space>
                              );
                            }}
                          />
                        </Space>
                      </div>
                    )}

                    <Button
                      type="primary"
                      onClick={handleInscribirme}
                      loading={inscribiendo}
                      disabled={
                        !listo
                        || (
                          Boolean(torneoSeleccionado.tor_elegir_horario_inscripcion)
                          && (horarioInscripcion.diaSemana == null || horarioInscripcion.franjaId == null)
                        )
                      }
                      block
                    >
                      {torneoSeleccionado.tor_con_senia ? 'Inscribirme y pagar seña' : 'Inscribirme'}
                    </Button>
                  </>
                );
              })()}
            </>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default JugadorMisTorneos;
